import { useCallback, useContext } from 'react';
import { AppContext } from '../../context/AppContext/context';
import { LoadedUser } from '../../interfaces';
import Emitter from '../../constants/emitter';

const eventEmitter = Emitter.getInstance();

/**
 * Hook to get the user data from the global state
 *
 */
function useUser<T extends LoadedUser | null>() {
  const { state, dispatch } = useContext(AppContext);

  const triggerReload = useCallback(() => {
    setTimeout(() => eventEmitter.emit('triggerUserReload'), 1);
  }, []);

  // wait for the user to be loaded - state.user.isUpdateTriggered is set to true when the user data is updated - need to find hacky way to do this
  const asyncTriggerReload = useCallback(async () => {
    // timeout after 5 seconds if `userReloaded` event is not emitted
    let timeout: NodeJS.Timeout | null = null;
    const reloaded = await new Promise(resolve => {
      triggerReload();

      timeout = setTimeout(() => {
        resolve(false);
      }, 5000);

      eventEmitter.once('userReloaded', () => resolve(true));
    });

    if (timeout) clearTimeout(timeout);

    return reloaded;
  }, [triggerReload]);

  const selectBusiness = useCallback(
    (businessId: string) => {
      if (businessId === state.user.selectedBusiness) return;
      dispatch({ type: 'SET_SELECTED_BUSINESS', payload: businessId });
      triggerReload();
    },
    [dispatch, state.user.selectedBusiness, triggerReload],
  );

  return {
    user: state.user.data as T,
    isLoading: state.user.isLoading,
    triggerReload,
    reloadUser: asyncTriggerReload,
    selectBusiness,
  };
}

export default useUser;
